import React, { useEffect, useState } from 'react';
import { ChevronDown } from 'lucide-react';

export const PRODUCTION_FAQ = [
  {
    q: 'Is there a minimum order?',
    a: 'Screen printing, embroidery, DTG, DTF, sublimation and print-on-demand have no minimum — order a single piece or a full run. Cut and sew has a minimum of 50 pieces per style / 50 pieces per color. Graphic design is quoted per project.',
  },
  {
    q: 'How long does production take?',
    a: 'Typical turnaround is 7–10 days. Your quote confirms the timeline for your order, and if you have a hard deadline, tell us on the quote form so we can plan around it.',
  },
  {
    q: 'Can I get a sample before the full run?',
    a: 'Yes — samples are available on request. Just mention it in the project details on the quote form.',
  },
  {
    q: 'What artwork files should I send?',
    a: 'Upload your logos or designs with the quote form as PNG, JPG, SVG, PDF, AI, EPS, PSD, TIFF or ZIP (up to 5 files, 25 MB each). If your artwork needs work, our graphic design service can prepare it for production.',
  },
  {
    q: 'Do you ship outside Los Angeles?',
    a: 'Yes. Everything is produced in-house in Los Angeles / Orange County and we ship nationwide — to you, your venue, your team or straight to your customers.',
  },
  {
    q: 'What are your payment terms?',
    a: 'Payment terms are confirmed on your quote, so you will know exactly what is due and when before production starts.',
  },
];

function FaqItem({ item, i }) {
  const [open, setOpen] = useState(i === 0);
  return (
    <li className="border-b border-white/10" data-testid={`faq-item-${i}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="w-full flex items-center justify-between gap-6 py-6 text-left" data-testid={`faq-toggle-${i}`}>
        <h3 className="font-display text-[#f4efe6] uppercase text-xl md:text-2xl tracking-tight">{item.q}</h3>
        <ChevronDown className={`w-5 h-5 flex-shrink-0 text-[#00b8ff] transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      <div className={`grid transition-[grid-template-rows] duration-300 ${open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
        <p className="overflow-hidden text-[#cfc8bb] leading-relaxed max-w-3xl">
          <span className="block pb-6">{item.a}</span>
        </p>
      </div>
    </li>
  );
}

// FAQ accordion + FAQPage JSON-LD (answers stay in the DOM so crawlers can read them).
export default function Faq({ items = PRODUCTION_FAQ }) {
  useEffect(() => {
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = 'faq-jsonld';
    el.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: items.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
    });
    document.head.appendChild(el);
    return () => el.remove();
  }, [items]);
  return (
    <section className="px-6 py-24 md:py-32 border-t border-white/10" data-testid="production-faq">
      <div className="max-w-[1100px] mx-auto">
        <h2 className="font-display text-[#f4efe6] uppercase text-5xl md:text-7xl tracking-tight mb-10">
          Production <span className="font-script text-[#00b8ff] normal-case italic">FAQ</span>
        </h2>
        <ul className="border-t border-white/10">
          {items.map((f, i) => <FaqItem key={f.q} item={f} i={i} />)}
        </ul>
      </div>
    </section>
  );
}
